/**
 * The client entry point.
 *
 * ── THE STYLESHEET ORDER IS LOAD-BEARING ─────────────────────────────────
 *   1. tokens.css   defines every custom property, scoped to [data-surface].
 *   2. base.css     the reset, the type scale, and the `e-` component layer
 *                   that primitives.tsx renders into.
 *   3. charts.css   the `e-plot` layer that charts.tsx renders into.
 *   4. gateway.css  the `eg-` layer the public pages render into. Last,
 *                   because it is the only layer that overrides a base rule
 *                   (the range input's focus outline), and because it is the
 *                   only one a page can be missing without noticing: it used
 *                   to be a template literal inside a component.
 *
 * Reversing 1 and 2 leaves every rule in base.css resolving var() against
 * nothing, which does not error: it renders a page of unstyled black text on
 * white with the correct layout, which is the hardest kind of CSS bug to spot
 * in review. charts.css is third because it overrides base.css's card padding
 * inside a plot, and a chart is always inside a card.
 *
 * StrictMode is on. It double-invokes effects in development, which is
 * exactly what surfaces the two request loops this surface could plausibly
 * have: Join's code resolution and the dashboard's popstate listener. Both
 * were written against it.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "~/app/styles/tokens.css";
import "~/app/styles/base.css";
import "~/app/components/charts.css";
import "~/app/styles/gateway.css";

import { App } from "~/app/App";

const container = document.getElementById("root");

if (container === null) {
  // Not a thrown error: a blank page with a console trace is indistinguishable
  // from a network failure to whoever is looking at it.
  document.body.textContent =
    "The page could not start because its mount point is missing. This is a build problem, not a network one.";
} else {
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
